import { useCallback, useEffect, useMemo, useRef } from "react";
import { Easing, Group, Tween } from "@tweenjs/tween.js";
import * as THREE from "three/webgpu";

import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { pass } from "three/tsl";
import { bloom } from "three/addons/tsl/display/BloomNode.js";

import { CARD_W, CARD_H, CARD_RADIUS } from "./forgeWikiLayouts";
import { drawCardFace, forgetCardInk, readColor, resolveTint } from "./forgeCardFace";
import { useForgeStageCore } from "./useForgeStageCore";
import { useAppearance } from "../../appearance";

/**
 * The same stage, drawn by the GPU, through a camera of its own.
 * ---------------------------------------------------------------------------
 * The cards are quads in a WebGPU scene instead of elements in a document.
 * Everything that decides what a card IS and where it goes - the arrangements,
 * the tweens, the selection, the search, the shelf - is useForgeStageCore, the
 * same code the DOM stage runs. What this file owns is the lens and where it
 * stands.
 *
 * ONE LENS, 42°, AND IT NEVER CHANGES
 * -----------------------------------
 * This used to borrow the CSS stage's camera so the two renderers framed the
 * scene identically: `fov = 2·atan(H / 2·PERSPECTIVE)`, a flat scale for the
 * magnification, the camera pinned at a distance the arrangement chose. It
 * agreed to the pixel and it had one bad property - the lens changed. Make the
 * window shorter and the focal length changed with it, so the perspective got
 * wider as the stage got smaller and every card leaned a little further out.
 * The arrangements made it worse: each one brought its own distance AND its
 * own zoom to make up for the distance, which is a second lens on top of the
 * first.
 *
 * So there is one lens now, FOV is a constant, and nothing is allowed to touch
 * it - not a resize, not an arrangement, not the zoom. A resize changes the
 * aspect and nothing else; getting closer to something means MOVING CLOSER to
 * it, which is what a camera does and what makes the perspective hold still.
 *
 * ORBIT CONTROLS
 * --------------
 * ...and with a camera that moves, the orbit is three.js's own: drag turns it
 * about the target, right-drag pans the target, the wheel dollies in and out
 * between `minDistance` and `maxDistance`. Damped, so it carries a little.
 *
 * The stage's own camera work is written against it rather than around it:
 *
 *   fit        solve the distance at which every card is inside the frustum,
 *              from the direction the camera is already looking
 *   focus      stand square to one card, at the distance that draws it the
 *              size the page asks for
 *   into view  move the target, keeping the distance and the direction
 *   the tour   walk the azimuth and the polar angle
 *
 * and each of them tweens the camera and the target, so the controls pick up
 * from wherever the movement ended.
 *
 * WHAT IS STILL THE DOM
 * ---------------------
 * The card being read closely. A texture at twelve times life size is mush,
 * and the controls on an opened card have to be clickable - so the page lays
 * the real card over the canvas, at the place this camera projects it to.
 *
 * COORDINATES
 * -----------
 * The arrangements are shared with the CSS stage and so they are written in
 * CSS's axes, where y runs DOWN the screen. A scene's runs up, so every y is
 * negated here and with it the rotations a mirror reverses - rx and rz, but
 * not ry. Done in this file rather than in the layouts: a layout module that
 * knew which renderer was asking would be the beginning of two scenes.
 * ---------------------------------------------------------------------------
 *
 * @param stageRef  the viewport, which clips and carries the pointer
 * @param tiles     one per card: { id, name, repo, goal, when, vulns, sev, docs }
 * @param looks     one per card: { tint, opacity, light } - what the DOM card
 *                  would have been painted in, so a palette or a card-fill
 *                  setting reaches both renderers from one place
 * @param query     what the search is pointing at, for the ones it passed over
 * @param selected  the card the keyboard is on
 */


/* The lens, and it is a constant.
   -------------------------------------------------------------------------
   Forty-two degrees, whatever the window is doing. Nothing below is allowed
   to write to `camera.fov`: a resize sets the aspect, getting closer to a
   card moves the camera, and the arrangement's own idea of where a
   photographer should stand is ignored entirely - it was solved for a lens
   that could change, and this one cannot. */
const FOV = 42;
const DEG = Math.PI / 180;
/* half the frustum's height at one unit of depth - the whole of the lens */
const TAN_V = Math.tan((FOV * DEG) / 2);

/* How big a card is drawn when it is brought up to be read.
   ---------------------------------------------------------------------------
   It used to be one number - 400px on the card's long side, whatever else was
   on screen - and that is only the right number at one zoom. A shelf of a
   hundred and twenty eight machines draws its cards at about 120px, so the
   card being read came up three and a half times the size of its neighbours
   and sat on top of a three-by-two block of them. What the shelf is for is
   seeing the shelf.

   So the size follows the shelf: a card comes up a little bigger than a card
   currently looks, which with its place at the top of the stage and its turn
   square to the viewer is enough to say "this one" - and the size is the one
   part of that which costs the neighbours anything, so it is kept to a tenth.
   A quarter, which is where this started, still put the card over the two
   beside it on a shelf drawn small.

   The floor is there so the overlay is never a speck, and the ceiling so a
   stage that is zoomed right into one card does not blow it past the frame.
   Neither is a size anybody reads at: what is being read is the panel. */
const FOCUS_MAG = 1.1;
const FOCUS_MIN = 96;
const FOCUS_MAX = 320;

/* How a card comes out of the shelf when it is chosen.
   -------------------------------------------------------------------------
   The camera used to travel to the card. It worked and it read as a zoom -
   the whole scene swung and the chosen card happened to end up in the middle
   of it. Moving the CARD instead reads as what it is: that one has been
   picked up and brought over, and everything else stayed where it was.

   So the camera does not move at all. The card leaves its place in the
   arrangement, arcs out on a path of its own and turns square to the viewer,
   and its home is never stored anywhere because the arrangement still holds
   it - letting go is the same journey run backwards. */
const FLY_MS = 820;
/* how far off a straight line the arc bows, as a share of the distance - and
   how much of a turn it takes on the way, both drawn once per flight so no
   two cards come over the same way */
const FLY_BEND = 0.34;
const FLY_SPIN = 0.75;

/* ...and where it stops. It used to stop dead ahead of the camera, in the
   middle of the stage - which is the one place the page cannot spare, because
   what is underneath a card that has been brought over is everything the page
   has to say about it. So it goes to the top and leaves the rest clear. This
   is the gap above it, in pixels, so it is not flush against the edge. */
const FLY_TOP_PAD = 24;

/* Smooth at both ends: a card that starts and stops abruptly reads as a
   teleport with a delay in it. */
const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - ((-2 * t + 2) ** 3) / 2);

/* Near and far are wide: a sphere of three hundred cards is some thousands of
   units deep and the camera may stand well back from it. */
const NEAR = 1;
const FAR = 20000;

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

function prefersReducedMotion() {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch (e) {
    return false;
  }
}

/* How finely a face is drawn, against how many of them there are.
   -------------------------------------------------------------------------
   A face is a texture, and a texture is memory: a card at twice its size is
   560x248, which is half a megabyte, which is a hundred and fifty of them for
   a page of three hundred cards and more than a gigabyte for one of a
   thousand. So the sharpness steps down as the shelf grows - nobody reads a
   card on a shelf of a thousand, they pick one out and open it, and the one
   they open is a DOM card at full sharpness anyway. */
function sharpness(n) {
  if (n <= 400) return 2;
  if (n <= 900) return 1.5;
  return 1;
}

/**
 * A card, as a solid with the card's own corners.
 * ---------------------------------------------------------------------------
 * A BoxGeometry was the obvious shape and the wrong one: the drawing on its
 * front has rounded corners, so a square box behind it showed four little tabs
 * of edge colour sticking out past the artwork. The outline has to be the same
 * shape as the picture.
 *
 * So the card is EXTRUDED from its own outline - the rounded rectangle the
 * face is drawn inside - which gives it the corners in the only place they
 * matter, around its silhouette. The depth edges stay square: a card is cut
 * from something, not moulded, and rounding those as well turns it into a
 * lozenge.
 *
 * Two things have to be put right afterwards, both because ExtrudeGeometry is
 * built for a general shape rather than for a picture frame:
 *
 *   the UVs     its own generator maps the lids in SHAPE coordinates, so the
 *               texture would be sampled from -140..140 rather than 0..1
 *   the groups  it puts both lids in one group, back first then front, so
 *               the drawing would land on the back of the card as well -
 *               mirrored. The group is cut in half and only the front keeps
 *               the face material.
 * ---------------------------------------------------------------------------
 */
function cardSolid(depth) {
  const w = CARD_W / 2;
  const h = CARD_H / 2;
  const r = Math.min(CARD_RADIUS, w, h);

  const outline = new THREE.Shape();
  outline.moveTo(-w + r, -h);
  outline.lineTo(w - r, -h);
  outline.quadraticCurveTo(w, -h, w, -h + r);
  outline.lineTo(w, h - r);
  outline.quadraticCurveTo(w, h, w - r, h);
  outline.lineTo(-w + r, h);
  outline.quadraticCurveTo(-w, h, -w, h - r);
  outline.lineTo(-w, -h + r);
  outline.quadraticCurveTo(-w, -h, -w + r, -h);

  /* u and v straight off the card's own box, which is what the face canvas
     was drawn in. The walls take the material with no map on it, so theirs
     only has to be valid. */
  const uv = {
    generateTopUV: (geometry, vertices, a, b, c) => [a, b, c].map(i => new THREE.Vector2(
      (vertices[i * 3] + w) / CARD_W,
      (vertices[i * 3 + 1] + h) / CARD_H,
    )),
    generateSideWallUV: () => [
      new THREE.Vector2(0, 0), new THREE.Vector2(1, 0),
      new THREE.Vector2(1, 1), new THREE.Vector2(0, 1),
    ],
  };

  const geometry = new THREE.ExtrudeGeometry(outline, {
    depth,
    bevelEnabled: false,
    /* five a corner: enough that the curve reads at the size a card is ever
       seen, few enough that three hundred of them are nothing */
    curveSegments: 5,
    UVGenerator: uv,
  });

  /* Extruded from z=0 forwards; the card's middle is where its position is. */
  geometry.translate(0, 0, -depth / 2);

  const lids = geometry.groups.find(g => g.materialIndex === 0);
  const walls = geometry.groups.find(g => g.materialIndex === 1);
  if (lids) {
    const each = lids.count / 2;
    geometry.clearGroups();
    // the back lid, then the front: the front is the one with the drawing
    geometry.addGroup(lids.start, each, 1);
    geometry.addGroup(lids.start + each, each, 0);
    if (walls) geometry.addGroup(walls.start, walls.count, 1);
  }

  return geometry;
}

/* ------------------------------------------------- what a card is carrying */

/* The four, worst first, and the colours they are cut from. Resolved off the
   document once rather than written down, like everything else that has a
   token (see forgeCardFace). */
const SEV_KEYS = ["critical", "high", "medium", "low"];

/* One slot per severity across the card's own width, inside its padding.
   -------------------------------------------------------------------------
   A counter is a small SQUARE now rather than a bar across its slot, and it
   stands exactly over the swatch the drawn face puts at the head of each
   count - so the thing coming out of the card and the thing printed on it are
   the same mark, and the number beside it is left legible instead of being
   covered by its own counter.

   The two numbers below are the face's own measurements (forgeCardFace draws
   the swatch at PAD + i*span, 6 wide, on a strip PAD + 9 up from the bottom);
   they are repeated rather than imported because that file draws in canvas
   pixels, top down, and this one places objects in world units, bottom up. */
const SEV_PAD = 14;
const SEV_SLOT = (CARD_W - SEV_PAD * 2) / SEV_KEYS.length;
const SEV_SIDE = 16;
const SEV_RADIUS = 4;
/* the middle of the i-th swatch, and the height they all sit at */
const sevX = (i) => -CARD_W / 2 + SEV_PAD + SEV_SLOT * i + 3;
const SEV_Y = CARD_H / 2 - (CARD_H - SEV_PAD - 9 + 4.5);
/* how far out of the card the biggest count comes, and the least any of them
   does - a count of one still has to be visible from the side */
const SEV_OUT = 34;
const SEV_MIN = 5;

/**
 * A rounded square slab, one unit deep and centred on the origin.
 *
 * Depth is left at one and scaled per card, so a count changing costs a
 * number rather than a geometry.
 */
function roundedSlab(side, radius, segments = 4) {
  const half = side / 2;
  const r = Math.min(radius, half);
  const outline = new THREE.Shape();
  outline.moveTo(-half + r, -half);
  outline.lineTo(half - r, -half);
  outline.quadraticCurveTo(half, -half, half, -half + r);
  outline.lineTo(half, half - r);
  outline.quadraticCurveTo(half, half, half - r, half);
  outline.lineTo(-half + r, half);
  outline.quadraticCurveTo(-half, half, -half, half - r);
  outline.lineTo(-half, -half + r);
  outline.quadraticCurveTo(-half, -half, -half + r, -half);

  const geometry = new THREE.ExtrudeGeometry(outline, {
    depth: 1,
    bevelEnabled: false,
    curveSegments: segments,
  });
  geometry.translate(0, 0, -0.5);
  return geometry;
}

/**
 * A colour off the document, onto a material.
 * ---------------------------------------------------------------------------
 * `Color.setRGB` takes its numbers in the WORKING colour space, which is
 * linear - and everything read off a stylesheet is sRGB. Handing one to the
 * other without saying so is a silent brightening: the estate's near-black
 * #0a0a0a came out as a mid grey, and a shelf of ten distinct dark tints came
 * out as ten barely distinguishable light ones. The fourth argument is the
 * whole fix.
 */
function paintOn(colour, rgb, k = 1) {
  if (!rgb) return;
  colour.setRGB((rgb.r / 255) * k, (rgb.g / 255) * k, (rgb.b / 255) * k, THREE.SRGBColorSpace);
}

/* The glass's own green, as the document defines it - asked once, and
   forgotten with the rest of the tokens when the theme turns over. */
let glassInk = null;
function glassEdge() {
  if (!glassInk) glassInk = resolveTint("var(--glass-edge, rgb(164, 226, 205))");
  return glassInk;
}

/* A severity's colour, as the document defines it - asked once each. */
const sevInk = new Map();
function sevColour(key) {
  if (!sevInk.has(key)) sevInk.set(key, resolveTint(`var(--sev-${key}, #888)`));
  return sevInk.get(key);
}

/**
 * How far a count pushes its box out of the card.
 *
 * Logarithmic, because the counts are not on one scale: a machine with four
 * findings and one with two hundred and seventy are both ordinary, and a
 * linear bar would draw the first as nothing. The log puts them within a
 * factor of three of each other, which is what makes a shelf of these
 * comparable at a glance.
 */
function sevOut(n) {
  if (!n) return SEV_MIN;
  return SEV_MIN + SEV_OUT * Math.min(1, Math.log10(1 + n) / Math.log10(301));
}

/**
 * The four boxes, built once per card and re-sized per card afterwards.
 *
 * They stand along the bottom of the card and come OUT of it, towards the
 * viewer - which is the whole reason they are objects rather than four more
 * numbers printed on the face. A shelf of three hundred cards seen at an
 * angle shows its worst machines as the ones with things sticking out of
 * them, before a single number has been read.
 */
function severityBoxes(depth, Material, lit) {
  const group = new THREE.Group();
  group.name = "sevs";

  SEV_KEYS.forEach((key, i) => {
    const mat = new Material({
      color: 0x808080,
      transparent: true,
      ...(lit ? { roughness: 0.4, metalness: 0.1 } : {}),
    });
    const box = new THREE.Mesh(roundedSlab(SEV_SIDE, SEV_RADIUS), mat);
    box.name = key;
    box.frustumCulled = false;
    /* across: over its own swatch on the drawn face
       down:   on the strip that face lays its counts along
       out:    set per card from the count - see sevOut */
    box.position.x = sevX(i);
    box.position.y = SEV_Y;
    box.position.z = depth / 2;
    group.add(box);
  });

  return group;
}

/* The arrangements are written in CSS's axes, where y runs down the screen. */
const place = (o, into) => into.set(o.x, -o.y, o.z);
const facing = (o, into) => into.set(-o.rx, o.ry, -o.rz, "YXZ");

/* The scene that is up, for the one thing that is asked for from outside the
   React tree: a picture of it. Module-level because the button that asks is in
   the toolbar, several components away, and threading a renderer through three
   pages to reach it would be worse than this. Set when the scene is built and
   cleared when it is taken down, so there is never a stale one to shoot. */
let onStage = null;

/** Is there a scene to take a picture of? */
export const canShoot = () => !!(onStage && onStage.ready);

/**
 * A high-resolution JPEG of the card view as it stands.
 * ---------------------------------------------------------------------------
 * The same camera, the same arrangement, the same lights - just drawn at
 * several times the pixels. Nothing about the framing changes: the pixel
 * ratio goes up and the CSS size stays where it is, so what comes out is what
 * is on screen, with the type and the card edges resolved rather than
 * reframed.
 *
 * Two things have to be put right for a JPEG in particular. It has no alpha,
 * and this canvas is drawn without a background - so a ground is put behind
 * the scene for the one frame, the page's own, or the cards would come out on
 * black whatever the theme. And the picture has to be taken in the same task
 * as the draw: a WebGPU canvas is presented and gone by the next one.
 * ---------------------------------------------------------------------------
 */
export async function shootCardView({ scale = 3, quality = 0.94 } = {}) {
  const w = onStage;
  if (!w || !w.ready) return null;

  const { renderer, scene, camera, canvas, post } = w;
  const size = renderer.getSize(new THREE.Vector2());
  const was = renderer.getPixelRatio();

  /* as many pixels as the device can hold a texture of */
  const LIMIT = 8192;
  const up = Math.max(1, Math.min(scale, LIMIT / Math.max(size.x, size.y, 1)));

  const ground = scene.background;
  const paper = readColor(resolveTint("var(--forge-page-bg, #060606)"));
  if (paper) scene.background = new THREE.Color(paper.r / 255, paper.g / 255, paper.b / 255);

  renderer.setPixelRatio(up);
  renderer.setSize(size.x, size.y, false);

  try {
    if (post) post.render();
    else renderer.render(scene, camera);
    const shot = await new Promise(done => canvas.toBlob(done, "image/jpeg", quality));
    return shot ? { blob: shot, width: canvas.width, height: canvas.height } : null;
  } finally {
    scene.background = ground;
    renderer.setPixelRatio(was);
    renderer.setSize(size.x, size.y, false);
    w.dirty = true;
  }
}

export function useForgeGpuStage(stageRef, {
  tiles, looks, query = "", selected = -1, enabled = true,
  /* the card being read, and the element the page lays over the canvas for it */
  overlayRef, overlayIndex = -1,
  /* What a card ANSWERS, which in the other renderer is the card's own React
     handlers: there is no element to hang them on here, so the page's are
     handed over and the ray decides which card they are about. */
  onPick, onOpen, onHover, onMenu,
  /* while true every card turns to face the screen rather than the way its
     arrangement put it - blended, never jumped */
  follow = false,
  /* the hand on the camera, so the page can drop out of the tour */
  onInteract,
  /* ...and the count, which is also the signal that the stage has an element:
     the page only draws it once there are cards to put on it, and a ref does
     not change identity when its element mounts. */
  count = 0,
  ...options
}) {
  const world = useRef(null);
  const tex = useMemo(() => sharpness(tiles.length), [tiles.length]);

  /* How far IN the camera may come, which is somebody's setting rather than a
     constant: Appearance > Max zoom. With a fixed lens that is a distance and
     not a scale - the closest the camera may stand - so it is worked out
     against the stage's height each time it is applied. */
  const [maxZoom] = useAppearance("zoom");

  /* How thick a card is, and whether anything is lighting it. Both change
     what the scene is MADE of - the geometry, the materials, the lights, the
     frame's way out - so both rebuild it rather than being pushed onto what
     is already there. A deliberate, occasional action; a rebuild is a couple
     of hundred milliseconds and goes through the same path that was already
     the only tested way to take this scene down. */
  const [depth] = useAppearance("depth");
  const [lights] = useAppearance("lights");
  const lit = lights >= 1;

  /* ...and whether a card is a painted surface or a pane of glass. This one
     changes only what is PAINTED - the face, and what colour the sides are -
     so it repaints rather than rebuilding. */
  const [glassOn] = useAppearance("glass");
  const glass = glassOn >= 1;

  /* What the surface is asked about while a pointer is on it - held in refs
     because the handlers are bound once, by the core, and must see the list
     that is current rather than the one that was. */
  const held = useRef({ tiles, looks, query, selected, tex, overlayIndex, follow, maxZoom });
  held.current = { tiles, looks, query, selected, tex, overlayIndex, follow, maxZoom };

  /* The fit, reached through a ref.
     -----------------------------------------------------------------------
     The scene is rebuilt whenever the depth or the lights change, and a fresh
     scene has to be framed the moment its renderer comes up. It cannot hold
     the fit directly: the fit is declared below, and an effect that handed it
     over would not run again on a rebuild, which left a new scene sitting at
     whatever distance the camera was constructed with. */
  const fitRef = useRef(null);

  /* The core is built below - it needs the surface, and the surface needs to
     be able to ask it where the cards are going. */
  const core = useRef(null);
  const places = () => {
    const c = core.current;
    if (!c) return [];
    const going = c.targets.current;
    const standing = c.objects.current;
    return going.length === standing.length ? going : standing;
  };

  /* ---- the renderer, the lens, the orbit ---- */
  useEffect(() => {
    const stage = stageRef.current;
    if (!enabled || !stage) return undefined;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(FOV, 1, NEAR, FAR);
    camera.position.set(0, 0, 3000);

    const canvas = document.createElement("canvas");
    canvas.className = "forge-wiki-stage__canvas";
    stage.appendChild(canvas);

    const renderer = new THREE.WebGPURenderer({ 
      canvas,
       antialias: true,
       logarithmicDepthBuffer: true,
       alpha: true
      });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));

    /* Lit, the frame is a photograph and is graded like one: a film curve on
       the way out, so the highlights along the card edges roll off instead of
       clipping to white. Unlit, nothing touches the colours - a card is its
       own tint and that is the whole of it. */
    if (lit) {
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      /* Under a film curve a dark scene LIFTS: the estate's tints are nearly
         black and ACES walks them up into a warm grey, which turned a shelf
         of ten distinct greys into one mauve sheet. The exposure is pulled
         back until the blacks are black again and the curve is only doing
         what it was brought in for - holding the highlights on the edges. */
      renderer.toneMappingExposure = 0.85;
    }

    /* The lights.
       ---------------------------------------------------------------------
       A key over the viewer's shoulder, a soft fill opposite it so the dark
       side of a turned card does not go to black, and a rim in the product's
       own accent from behind - which is what picks out the edges of a hundred
       cards at once, and is most of why the depth reads at all.

       Hung on the CAMERA rather than on the scene, so the light comes from
       where the viewer is standing however the orbit is turned. On the scene
       instead, half a sphere of cards would be in shadow the moment somebody
       walked round it. */
    if (lit) {
      /* The intensities add up to about one.
         -----------------------------------------------------------------
         A lit material multiplies its own colour by what reaches it, so the
         sum of these decides whether a card still looks like its own tint or
         like whatever the lamps are made of. Four lights at the strengths
         that look right one at a time come to five between them, and a shelf
         lit at five is a shelf with no colours of its own left. */
      const key = new THREE.DirectionalLight(0xfff4e8, 1.4);
      key.position.set(0.45, 0.8, 1);
      const fill = new THREE.DirectionalLight(0xdde6ff, 0.35);
      fill.position.set(-0.8, -0.3, 0.6);
      /* ...and the rim is the one that is allowed to be a colour, because it
         only ever catches an edge: it comes from behind the scene, so what it
         lights is the side of a turned card and the lip of a flat one. */
      const rim = new THREE.DirectionalLight(0xff5e19, 0.85);
      rim.position.set(-0.3, 0.5, -1);
      camera.add(key, fill, rim, new THREE.AmbientLight(0xffffff, 0.9));
      scene.add(camera);
    }

    /* The controls listen on the STAGE, not on the canvas: the canvas takes no
       pointer events (it would swallow the card being read, which is a real
       element over it), and the stage is where the hand already is. */
    const controls = new OrbitControls(camera, stage);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.rotateSpeed = 0.6;
    controls.zoomSpeed = 0.9;
    controls.panSpeed = 0.7;
    controls.screenSpacePanning = true;
    /* Stopping short of the poles: past one the scene rolls over, which reads
       as the drag having broken something. */
    controls.minPolarAngle = 0.12;
    controls.maxPolarAngle = Math.PI - 0.12;
    controls.minDistance = 1;
    controls.maxDistance = FAR / 4;

    /* ...and the frame's way out. Unlit it goes straight to the canvas; lit it
       goes through a bloom first, which is what makes a lit edge read as a lit
       edge rather than as a pale line. */
    let post = null;
    if (lit) {
      const shot = pass(scene, camera);
      const colour = shot.getTextureNode("output");
      post = new THREE.PostProcessing(renderer);
            /* A threshold high enough that only a lit edge passes it: a bloom that
         takes the whole frame is a blur, not a glow. */
      post.outputNode = colour.add(bloom(colour, 0.22, 0.45, 0.92));
    }

    const state = {
      scene, camera, renderer, canvas, controls, post,
      cards: [], bodies: [], faces: [], ready: false, alive: true,
      raycaster: new THREE.Raycaster(),
      pointer: new THREE.Vector2(),
      tweens: new Group(),
      /* the last thing the core painted, kept so the camera can move without
         it and the cards still come out facing the right way */
      at: [], blend: 0,
      reading: -1,
      hover: -1, hoverWas: -1,
      fly: null,
      dirty: true,
      touring: false,
      tourAt: 0,
      /* scratch, so a frame allocates nothing */
      v: new THREE.Vector3(),
      e: new THREE.Euler(),
      q: new THREE.Quaternion(),
    };
    world.current = state;
    onStage = state;

    const size = () => {
      const w = stage.clientWidth || 1;
      const h = stage.clientHeight || 1;
      /* the aspect, and ONLY the aspect: the lens is a constant */
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h, false);
      state.dirty = true;
    };

    /* The hovered card's face is redrawn every frame, because the light
       running round it has moved. One card, one 560x248 canvas: the rest of
       the shelf is untouched and the texture upload is a single card's worth.
       When the pointer leaves, it is drawn once more without the light. */
    const SWEEP_MS = 1800;
    const sweeping = (now) => {
      if (state.hoverWas !== state.hover) {
        if (state.hoverWas >= 0) state.one?.(state.hoverWas, null);
        state.hoverWas = state.hover;
      }
      if (state.hover < 0) return false;
      state.one?.(state.hover, ((now % SWEEP_MS) / SWEEP_MS) * Math.PI * 2);
      return true;
    };

    /* Where a card is held when it has been brought over: straight ahead of
       the camera, at the distance that draws it the size the page asks for.
       Recomputed every frame rather than fixed at the start, so a card that
       is being read stays in front of the viewer while the orbit turns -
       which is the whole of "it came to me". */
    const held2 = new THREE.Vector3();
    const ahead2 = new THREE.Vector3();
    const up2 = new THREE.Vector3();
    const home = new THREE.Vector3();
    const bend = new THREE.Vector3();
    const spin = new THREE.Quaternion();
    const qHome = new THREE.Quaternion();

    /* How big the card being read should come up, in pixels on its long side:
       a quarter more than that card looks WHERE IT LIVES. Measured from its
       place in the arrangement rather than from where it is being held, so
       there is nothing circular about it - and measured every frame, so
       zooming the shelf while reading a card keeps the two in proportion. */
    const box2 = new THREE.Vector3();

    const focusBox = (h) => {
      const at = held.current.overlayIndex;
      const o = at >= 0 ? state.at[at] : null;
      if (!o) return FOCUS_MAX;

      camera.updateMatrixWorld();
      place(o, box2).applyMatrix4(camera.matrixWorldInverse);
      const depth = -box2.z;
      if (!(depth > NEAR)) return FOCUS_MAX;

      const seen = Math.max(CARD_W, CARD_H) * ((h / 2) / (depth * TAN_V));
      return Math.max(FOCUS_MIN, Math.min(FOCUS_MAX, seen * FOCUS_MAG));
    };

    const holdPoint = (into) => {
      const h = stage.clientHeight || 700;
      const big = Math.max(CARD_W, CARD_H);
      const box = focusBox(h);
      const far = (big * h) / (2 * box * TAN_V);
      ahead2.set(0, 0, -1).applyQuaternion(camera.quaternion);
      into.copy(camera.position).addScaledVector(ahead2, far);

      /* ...and then up, to the top of the stage. The card is drawn `box`
         across at this distance, which fixes both its height on screen and
         how many world units a pixel is worth there - so the lift is however
         much room is left between the middle of the stage and a card sitting
         at the top of it, read back into the world.

         Along the CAMERA's up rather than the world's: the orbit turns under
         a card that is being read, and the whole point of recomputing this
         every frame is that the card stays where the viewer put it. */
      const perPx = big / box;
      const cardH = (CARD_H * box) / big;
      const lift = Math.max(0, h / 2 - cardH / 2 - FLY_TOP_PAD);
      up2.set(0, 1, 0).applyQuaternion(camera.quaternion);
      return into.addScaledVector(up2, lift * perPx);
    };

    /* The flight, moved on by the clock. A card is let go the moment the page
       stops calling it the chosen one - by Escape, by another card, or by
       being clicked again - so there is nothing to subscribe to: the return
       starts here, when the two disagree. */
    const flying = () => {
      const f = state.fly;
      if (!f) return null;

      const wanted = held.current.overlayIndex;
      if (f.dir > 0 && wanted !== f.index) {
        f.dir = -1;
        /* turn round from wherever it had got to, not from the start */
        f.at = performance.now() - (1 - f.p) * FLY_MS;
      } else if (f.dir < 0 && wanted === f.index) {
        f.dir = 1;
        f.at = performance.now() - f.p * FLY_MS;
      }

      const gone = Math.min(1, (performance.now() - f.at) / FLY_MS);
      f.p = f.dir > 0 ? gone : 1 - gone;
      f.t = ease(f.p);

      if (gone < 1) state.dirty = true;
      else if (f.dir < 0) { state.fly = null; return null; }
      return f;
    };

    const draw = () => {
      const n = Math.min(state.shown || 0, state.at.length);
      const flight = flying();
      const blend = state.blend;
      const bill = blend > 0.0005;
      const reading = held.current.overlayIndex;

      /* While a card is travelling, the quad IS the card: the real one laid
         over the scene is standing down until it lands (see overlay), so
         hiding the quad as well would leave nothing on its way over at all. */
      const shown = !state.fly || state.fly.t > 0.985;

      /* The chosen card is drawn over its neighbours rather than through
         them: it is 40% bigger, so in a flat arrangement it now overlaps
         them, and two coplanar quads have no answer to which is in front. A
         nudge along the camera's own axis settles it - towards the eye, so it
         is the one that wins, and small enough that nothing about where the
         card is has visibly changed. */
      const lift = state.v.copy(camera.position).sub(controls.target).normalize();
      const chosen = held.current.selected;

      for (let i = 0; i < n; i++) {
        const o = state.at[i];
        const mesh = state.cards[i];
        if (!o || !mesh) continue;
        /* The card being read is drawn twice - once as a quad and once as the
           real card over it - and a card's surface is translucent, so the two
           would stack into a tint darker than either. The quad stands down. */
        mesh.visible = !(i === reading && shown);

        /* A card on its way over, or on its way back.
           -----------------------------------------------------------------
           Its place in the arrangement is still where it belongs - nothing
           was saved and nothing has to be put back - so the whole journey is
           a reading between that place and the point in front of the camera,
           and letting go is the same reading run the other way.

           The path is not a straight line: a control point pushed sideways
           bows it, and the card keeps turning about an axis of its own until
           it arrives, where the turn runs out and it is square to the
           viewer. Both the bow and the turn are drawn once per flight, so no
           two cards come over the same way. */
        if (flight && i === flight.index && flight.t > 0.0005) {
          const t = flight.t;
          place(o, home);
          if (i === chosen) home.addScaledVector(lift, 12);
          holdPoint(held2);

          /* the middle of a straight line, shoved aside */
          bend.copy(home).add(held2).multiplyScalar(0.5)
            .addScaledVector(flight.aside, home.distanceTo(held2) * FLY_BEND);

          /* one quadratic bend, read at t */
          const u = 1 - t;
          mesh.position.set(0, 0, 0)
            .addScaledVector(home, u * u)
            .addScaledVector(bend, 2 * u * t)
            .addScaledVector(held2, t * t);

          qHome.setFromEuler(facing(o, state.e));
          mesh.quaternion.copy(qHome).slerp(camera.quaternion, t);
          /* ...and the turn it takes on the way, which runs out on arrival */
          spin.setFromAxisAngle(flight.axis, flight.turns * Math.PI * 2 * (1 - t));
          mesh.quaternion.multiply(spin);

          /* its own size on the way out: the distance is doing the growing */
          const k = o.k === undefined ? 1 : o.k;
          mesh.scale.setScalar(k + (1 - k) * t);
          continue;
        }

        place(o, mesh.position);
        if (i === chosen) mesh.position.addScaledVector(lift, 12);
        mesh.scale.setScalar(o.k === undefined ? 1 : o.k);
        state.q.setFromEuler(facing(o, state.e));
        /* Follow mode: the quad takes the camera's own orientation, which is
           what puts it square to the screen. Worked out here, per frame,
           rather than tweened into the card, so it keeps facing us while the
           scene is being turned. */
        if (bill) mesh.quaternion.slerpQuaternions(state.q, camera.quaternion, blend);
        else mesh.quaternion.copy(state.q);
      }

      if (post) post.render();
      else renderer.render(scene, camera);
      overlay();
    };

    /* The one real card, put where this camera says that card is. */
    const overlay = () => {
      const over = overlayRef?.current;
      if (!over) return;
      const at = held.current.overlayIndex;
      const o = at >= 0 ? state.at[at] : null;
      /* While a card is travelling it is turning, and a DOM element laid flat
         over the scene cannot turn with it - so the real card waits until the
         flight has all but landed and the quad it replaces is square on. */
      const settled = !state.fly || state.fly.t > 0.985;
      if (!o || !settled) { over.style.visibility = "hidden"; return; }

      /* Where the card ACTUALLY is, which once it has been brought over is
         not where the arrangement says it belongs. */
      const flown = state.fly && state.fly.index === at;
      const seen = flown
        ? project({ x: state.cards[at].position.x, y: -state.cards[at].position.y, z: state.cards[at].position.z })
        : project(o);
      if (!seen) { over.style.visibility = "hidden"; return; }
      over.style.visibility = "visible";
      over.style.transform =
        `translate(${seen.x.toFixed(2)}px, ${seen.y.toFixed(2)}px) scale(${seen.s.toFixed(4)})`;
    };

    const project = (o) => {
      const h = stage.clientHeight || 1;
      camera.updateMatrixWorld();
      place(o, state.v).applyMatrix4(camera.matrixWorldInverse);
      const depth = -state.v.z;
      if (depth <= NEAR) return null;
      /* pixels per world unit at that depth - the whole of the projection,
         since the lens never changes */
      const s = (h / 2) / (depth * TAN_V);
      return { x: state.v.x * s, y: -state.v.y * s, s, depth };
    };
    state.project = project;

    /* WASD, while they are held down.
       ---------------------------------------------------------------------
       The orbit turns the camera ABOUT something; this moves the camera
       ITSELF, which is the other half of getting around a scene this size: W
       and S fly forward and back along the way the camera is looking, A and D
       slide left and right across it. The target travels with it, so the
       scene does not swing round - it is a walk, not a turn, and the orbit
       picks up from wherever the walk ended.

       Worked out per frame from what is held rather than per keydown, so the
       movement is smooth from the first frame instead of waiting on the
       operating system's key repeat; and scaled by how far the camera is
       standing, so one press covers the same FRACTION of the view whether the
       camera is reading one card or looking at all three hundred. */
    const step = new THREE.Vector3();
    const walk = (dt) => {
      const keys = state.keys;
      if (!keys || !keys.size) return false;

      const ahead = state.v.copy(controls.target).sub(camera.position);
      const reach = ahead.length() || 1;
      ahead.divideScalar(reach);
      const side = new THREE.Vector3().crossVectors(ahead, camera.up).normalize();

      step.set(0, 0, 0);
      if (keys.has("w")) step.addScaledVector(ahead, 1);
      if (keys.has("s")) step.addScaledVector(ahead, -1);
      if (keys.has("d")) step.addScaledVector(side, 1);
      if (keys.has("a")) step.addScaledVector(side, -1);
      if (!step.lengthSq()) return false;

      step.normalize().multiplyScalar(reach * 1.1 * dt);
      camera.position.add(step);
      controls.target.add(step);
      return true;
    };

    /* One loop, and it paints only when something moved. */
    let was = performance.now();
    const tick = (now = performance.now()) => {
      if (!state.alive) return;
      state.raf = requestAnimationFrame(tick);
      const dt = Math.min((now - was) / 1000, 0.1);
      was = now;
      state.tweens.update(now);
      const walked = walk(dt);
      const moved = controls.update();
      if (!state.ready) return;
      /* a light that is travelling is a reason to paint, like anything else
         that moved */
      const lit = sweeping(now);
      if (walked || moved || lit || state.dirty) { state.dirty = false; draw(); }
    };

    /* The keys themselves. On the window, because the stage is not focusable
       and nobody should have to click it first - and ignored the moment
       anything is being typed into, which is what the search field is. */
    state.keys = new Set();
    const WALK = ["w", "a", "s", "d"];
    const typing = (e) => {
      const el = e.target;
      return !!(el && el.closest && el.closest("input, textarea, [contenteditable='true']"));
    };

    const onKeyDown = (e) => {
      if (e.ctrlKey || e.altKey || e.metaKey || typing(e)) return;
      const k = e.key.toLowerCase();
      if (!WALK.includes(k)) return;
      e.preventDefault();
      if (!state.keys.size && state.touring) onInteract?.();
      state.keys.add(k);
      /* a walk is the hand on the camera: whatever it was pointed at, it is
         not being read any more */
      state.reading = -1;
      state.tweens.removeAll();
    };
    const onKeyUp = (e) => { state.keys.delete(e.key.toLowerCase()); };
    const letGo = () => state.keys.clear();

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", letGo);

    /* A hand on the camera ends the tour, the way it does on the other stage. */
    controls.addEventListener("start", () => {
      state.turning = true;
      if (state.touring) onInteract?.();
    });
    controls.addEventListener("end", () => { state.turning = false; });
    controls.addEventListener("change", () => { state.dirty = true; });

    state.fit = (ms) => fitRef.current?.(ms);

    renderer.init().then(() => {
      if (!state.alive) return;
      state.ready = true;
      size();
      state.dirty = true;
      state.fit(0);
    }).catch(() => { state.ready = false; });

    tick();

    /* A handle on the camera, beside the other things this app leaves on
       window.appfactor for a console to ask. The lens is the interesting one:
       it must read 42 whatever the window has been doing. */
    if (typeof window !== "undefined") {
      window.appfactor = {
        ...(window.appfactor || {}),
        cardStage: () => ({
          fov: camera.fov,
          aspect: Number(camera.aspect.toFixed(4)),
          distance: Number(camera.position.distanceTo(controls.target).toFixed(1)),
          target: controls.target.toArray().map(n => Number(n.toFixed(1))),
          between: [controls.minDistance, controls.maxDistance].map(n => Number(n.toFixed(1))),
          cards: state.shown || 0,
        }),
        /* the scene itself, for a console that wants to put something in it
           and see whether this renderer can draw it */
        cardScene: () => state,
        /* ...and the picture the toolbar's camera takes, for anything that
           wants one without a button to press */
        cardShot: (opts) => shootCardView(opts),
      };
    }

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(size);
      ro.observe(stage);
    }

    return () => {
      state.alive = false;
      cancelAnimationFrame(state.raf);
      clearTimeout(state.tourAt);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", letGo);
      ro?.disconnect();
      state.tweens.removeAll();
      controls.dispose();
      /* A texture disposed while the renderer still holds it leaves WebGPU
         with a dead handle - "invalid value used as weak map key", and then a
         stream of invalid command buffers - so the scene is taken down in one
         go, here, and never a card at a time. */
      state.cards.forEach(card => {
        card.traverse(o => {
          o.geometry?.dispose();
          if (Array.isArray(o.material)) o.material.forEach(x => x.dispose());
          else o.material?.dispose();
        });
        card.userData.skin?.texture?.dispose();
      });
      renderer.dispose?.();
      canvas.remove();
      world.current = null;
      if (onStage === state) onStage = null;
    };
  }, [stageRef, enabled, count, overlayRef, onInteract, lit, depth]);

  /* ---- where the camera stands ---- */

  /** Tween it to a place and a direction, leaving the controls holding it. */
  const travel = useCallback((target, distance, dir, ms) => {
    const w = world.current;
    if (!w) return;
    const from = {
      tx: w.controls.target.x, ty: w.controls.target.y, tz: w.controls.target.z,
      px: w.camera.position.x, py: w.camera.position.y, pz: w.camera.position.z,
    };
    const d = clamp(distance, w.controls.minDistance, w.controls.maxDistance);
    const to = {
      tx: target.x, ty: target.y, tz: target.z,
      px: target.x + dir.x * d, py: target.y + dir.y * d, pz: target.z + dir.z * d,
    };

    w.tweens.removeAll();
    const settle = () => {
      w.controls.target.set(from.tx, from.ty, from.tz);
      w.camera.position.set(from.px, from.py, from.pz);
      w.dirty = true;
    };

    if (!ms || prefersReducedMotion()) {
      Object.assign(from, to);
      settle();
      return;
    }
    new Tween(from, w.tweens).to(to, ms).easing(Easing.Exponential.InOut)
      .onUpdate(settle)
      .start();
  }, []);

  /** Which way the camera is looking now. */
  const heading = useCallback(() => {
    const w = world.current;
    const dir = new THREE.Vector3(0, 0, 1);
    if (!w) return dir;
    dir.copy(w.camera.position).sub(w.controls.target);
    return dir.lengthSq() < 1e-6 ? new THREE.Vector3(0, 0, 1) : dir.normalize();
  }, []);

  /** The closest the camera may stand, which is what Max zoom means here. */
  const nearest = useCallback(() => {
    const stage = stageRef.current;
    const h = stage?.clientHeight || 700;
    /* at (h/2)/TAN_V one world unit is drawn as one pixel - life size - so the
       setting's multiplier is simply how much closer than that it may come */
    return (h / 2) / (TAN_V * Math.max(1, held.current.maxZoom));
  }, [stageRef]);

  /**
   * Stand where every card is in frame, from the direction the camera is
   * already looking.
   *
   * Solved against the frustum rather than against a bounding sphere: a table
   * of cards is wide and flat, and the sphere around it would leave a third
   * of the stage empty. For each card, the distance at which it just fits is
   * its depth along the view plus its offset across it over the frustum's
   * tangent; the furthest of those is the answer.
   */
  const fit = useCallback((ms = 600) => {
    const w = world.current;
    const stage = stageRef.current;
    const all = places();
    if (!w || !stage || !all.length) return;

    const dir = heading();
    const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), dir);
    const right = new THREE.Vector3(1, 0, 0).applyQuaternion(q);
    const up = new THREE.Vector3(0, 1, 0).applyQuaternion(q);

    const centre = new THREE.Vector3();
    const p = new THREE.Vector3();
    all.forEach(o => centre.add(place(o, p)));
    centre.divideScalar(all.length);

    const tanH = TAN_V * ((stage.clientWidth || 1200) / (stage.clientHeight || 700));
    /* a card's reach is taken from its corner rather than its edge: turned,
       its footprint is somewhere between its width and its diagonal */
    const R = Math.hypot(CARD_W, CARD_H) / 2;

    let need = 0;
    all.forEach(o => {
      place(o, p).sub(centre);
      const z = p.dot(dir);
      need = Math.max(
        need,
        z + (Math.abs(p.dot(right)) + R) / tanH,
        z + (Math.abs(p.dot(up)) + R) / TAN_V,
      );
    });

    /* A hair of air, and room to go further out than the fit but not to
       nowhere. */
    w.controls.minDistance = nearest();
    w.controls.maxDistance = Math.max(need * 6, 2000);
    w.reading = -1;
    travel(centre, need * 1.02, dir, ms);
  }, [stageRef, heading, nearest, travel]);

  /**
   * Read one card closely: stand square to it, at the distance that draws it
   * the size the page asks for.
   *
   * The magnification is a DISTANCE now rather than a scale, which is the
   * whole point of a fixed lens - a card read close up is seen through the
   * same optics as the shelf it came from.
   */
  const focus = useCallback((index, { toggle = true } = {}) => {
    const w = world.current;
    const o = places()[index];
    if (!w || !o) return false;

    /* Already out, and asked again: that is a request to put it back. The
       flight itself is not reversed here - the frame loop sees that the page
       has stopped calling this card the chosen one and turns it round. */
    if (toggle && w.fly && w.fly.index === index && w.fly.dir > 0) {
      w.reading = -1;
      return false;
    }

    /* A different card chosen while one is out: the one that was out starts
       back on the next frame, for the same reason. */
    w.fly = {
      index,
      dir: 1,
      at: performance.now(),
      p: 0,
      t: 0,
      /* the two things that make this flight its own: which way the arc bows
         and which way the card turns on the way over */
      aside: new THREE.Vector3(
        Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5,
      ).normalize(),
      axis: new THREE.Vector3(
        Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5,
      ).normalize(),
      turns: FLY_SPIN * (0.6 + Math.random() * 0.8) * (Math.random() < 0.5 ? -1 : 1),
    };

    w.reading = index;
    w.dirty = true;
    return true;
  }, []);

  /**
   * Bring a card into view, if it is not already - the target moves, the
   * distance and the direction do not. This is the shelf being walked, not a
   * card being read.
   */
  const bringIntoView = useCallback((index, { margin = 28, duration = 420 } = {}) => {
    const w = world.current;
    const stage = stageRef.current;
    const o = places()[index];
    if (!w || !stage || !o || !w.project) return false;

    const seen = w.project(o);
    const W = (stage.clientWidth || 1200) / 2;
    const H = (stage.clientHeight || 700) / 2;

    if (seen) {
      const reach = (Math.hypot(CARD_W, CARD_H) / 2) * seen.s;
      const inside = Math.abs(seen.x) + reach <= W - margin
        && Math.abs(seen.y) + reach <= H - margin;
      if (inside) return false;
    }

    travel(
      place(o, new THREE.Vector3()),
      w.camera.position.distanceTo(w.controls.target),
      heading(),
      duration,
    );
    return true;
  }, [stageRef, heading, travel]);

  /** Square on to the arrangement again, and framed. */
  const resetCamera = useCallback(() => {
    const w = world.current;
    if (!w) return;
    w.camera.position.set(w.controls.target.x, w.controls.target.y, w.controls.target.z + w.camera.position.distanceTo(w.controls.target));
    w.dirty = true;
    fit(600);
  }, [fit]);

  /**
   * The tour: the camera walking the azimuth and the polar angle on its own.
   *
   * One tween at a time, each to an angle picked at random and over a random
   * few seconds, chained end to end - so the movement never repeats and never
   * arrives anywhere in particular. A hand on the controls ends it, which the
   * "start" listener above reports.
   */
  const tour = useCallback((on) => {
    const w = world.current;
    if (!w) return;
    w.touring = on;
    clearTimeout(w.tourAt);
    if (!on) { w.tweens.removeAll(); return; }

    const step = () => {
      const live = world.current;
      if (!live || !live.touring) return;

      const r = live.camera.position.distanceTo(live.controls.target);
      const sph = new THREE.Spherical().setFromVector3(
        new THREE.Vector3().copy(live.camera.position).sub(live.controls.target),
      );
      const from = { theta: sph.theta, phi: sph.phi };
      const to = {
        // a turn of between a third and a full half circle, either way round
        theta: sph.theta + (0.9 + Math.random() * 2.2) * (Math.random() < 0.5 ? -1 : 1),
        // and a tilt that stays well short of the poles
        phi: clamp(Math.PI / 2 + (Math.random() * 1.5 - 0.75), 0.3, Math.PI - 0.3),
      };
      const ms = 3400 + Math.random() * 3000;
      const at = new THREE.Vector3();

      new Tween(from, live.tweens).to(to, ms).easing(Easing.Quadratic.InOut)
        .onUpdate(() => {
          at.setFromSphericalCoords(r, from.phi, from.theta);
          live.camera.position.copy(live.controls.target).add(at);
          live.dirty = true;
        })
        .start();

      live.tourAt = setTimeout(step, ms);
    };
    step();
  }, []);

  /* ...and this is what that ref points at, from here on. */
  fitRef.current = fit;

  /* ---- one card per card ----
     A pool: when the list shrinks the extra cards are hidden rather than
     thrown away, and when it grows again they are taken back.

     A card is a SOLID when it has a depth and a quad when it has none - the
     solid cut out to the card's own rounded outline, see cardSolid. It
     carries two materials: the face, which is the drawing, and the edge,
     which is the back and the four sides in the card's own colour a shade
     down.

     Lit, the materials answer to the lights and the edges are what catch
     them; unlit they are their own colour and nothing else, which is the
     cheapest thing a GPU can draw. */
  useEffect(() => {
    const w = world.current;
    if (!w) return;

    const solid = depth > 0.5;
    const Material = lit ? THREE.MeshStandardMaterial : THREE.MeshBasicMaterial;

    while (w.cards.length < tiles.length) {
      const face = document.createElement("canvas");
      face.width = Math.round(CARD_W * tex);
      face.height = Math.round(CARD_H * tex);

      const texture = new THREE.CanvasTexture(face);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.anisotropy = 4;

      const skin = {
        map: texture,
        transparent: true,
        /* A quad has to show its back - half of a sphere faces away from the
           camera, and hiding those leaves the arrangement with holes in it.
           A box has a back of its own, so it only ever shows its outside. */
        side: solid ? THREE.FrontSide : THREE.DoubleSide,
        ...(lit ? { roughness: 0.52, metalness: 0.08 } : {}),
      };

      const faceMat = new Material(skin);
      const edgeMat = solid
        ? new Material({ ...skin, map: null, color: 0x202020 })
        : null;

      const body = new THREE.Mesh(
        solid ? cardSolid(depth) : new THREE.PlaneGeometry(CARD_W, CARD_H),
        /* the groups cardSolid leaves behind: 0 is the face it is drawn on,
           1 is the back and the four sides */
        solid ? [faceMat, edgeMat] : faceMat,
      );
      body.frustumCulled = false;

      /* A card is a GROUP now, not a mesh: it has a body, four boxes coming
         out of its face, and whatever is written on it. The group is what the
         arrangement moves and what the selection scales; the body is what the
         pointer is tested against, since a ray that hit the number four
         sticking out of a card should still mean that card. */
      const card = new THREE.Group();
      card.frustumCulled = false;
      card.add(body);

      const sevs = severityBoxes(solid ? depth : 0, Material, lit);
      card.add(sevs);

      const at = w.cards.length;
      card.userData.index = at;
      body.userData.index = at;
      /* kept by name, because `body.material` is a list once a card has sides
         and every caller would otherwise have to know which */
      card.userData.skin = { faceMat, edgeMat, texture, body, sevs };

      w.scene.add(card);
      w.cards.push(card);
      w.bodies.push(body);
      w.faces.push(face);
    }

    w.cards.forEach((m, i) => { m.visible = i < tiles.length; });
    w.shown = tiles.length;
    w.dirty = true;
  }, [tiles, tex, enabled, depth, lit]);

  /* ---- what is written on them ----
     Redrawn when the cards change, when the palette or the card fill does,
     when the theme turns over, and when the search moves - never per frame. */
  useEffect(() => {
    const w = world.current;
    if (!w) return;

    forgetCardInk();
    /* the severities are tokens too, and they turn over with the theme */
    sevInk.clear();
    glassInk = null;

    const q = query.trim().toLowerCase();

    /* A card whose mark has not arrived yet is drawn without it and drawn
       again when it has. One pass per image rather than per card, since a
       shelf of a hundred shares a dozen marks between them. */
    let again = 0;
    const redraw = () => {
      if (again) return;
      again = requestAnimationFrame(() => { again = 0; paintFaces(); });
    };

    /* One card's face, which is also what the light running round a hovered
       card needs every frame - so the whole shelf is not redrawn for it. */
    const one = (i, sweep) => {
      const mesh = w.cards[i];
      const tile = tiles[i];
      const look = looks[i];
      if (!mesh || !tile || !look) return;

      /* Two ways a card steps back - the search passed over it, or a lens is
         pointing somewhere else - and they mean the same thing: the CARD
         steps back, all of it. Not the drawing on its face, which is what
         this used to do and which left a faded picture on a solid block the
         moment a card had sides and four boxes coming out of it. It is a
         group of objects now, so the fade is a number every material in the
         group is multiplied by. */
      const hit = q ? (tile.hay || "").includes(q) : null;
      const back = (look.dim || hit === false) ? 0.3 : 1;

      drawCardFace(w.faces[i], tile, {
        tint: resolveTint(look["--forge-wiki-card-tint"]),
        opacity: Number(look["--forge-wiki-card-opacity"]) || 1,
        light: look.light !== false,
      }, {
        /* only for the outline it draws round a match - the stepping back is
           the group's business now */
        lit: hit,
        selected: i === selected,
        /* a card somebody has chosen is not also a card that is stepping
           back: it goes solid, whatever the fill setting says */
        full: i === selected,
        sweep,
        tex,
        glass,
        onReady: redraw,
      });
      /* ...and the sides take the card's own colour, a shade down, so a box
         reads as one object rather than as a drawing glued to a grey block. */
      const { faceMat, edgeMat, texture, sevs } = mesh.userData.skin;
      const weight = Number(look["--forge-wiki-card-opacity"]) || 1;

      faceMat.opacity = back;
      if (edgeMat) {
        if (glass) {
          /* The one thing only this renderer can do with the look.
             -------------------------------------------------------------
             A pane of glass is mostly its edge, and the DOM card has no
             edge to light: it is a hairline round a flat thing. Here a card
             is eighteen millimetres thick by default, so its four sides are
             real surfaces - and they are what the reference picture is
             actually of. They take the glass's own green, at full strength
             whatever the face is doing, so a shelf of panes reads as a
             shelf of objects catching the light along every edge.

             Lit, they also emit: with lights in the scene the edges are the
             only part of a pane that should glow, and an emissive side is
             the cheapest possible way to say so. */
          paintOn(edgeMat.color, readColor(glassEdge()));
          edgeMat.opacity = Math.min(1, 0.52 + weight * 0.4) * back;
          if (edgeMat.emissive) {
            paintOn(edgeMat.emissive, readColor(glassEdge()));
            edgeMat.emissiveIntensity = 0.55;
          }
        } else {
          /* ...and the sides take the card's own colour, a shade down, so a
             card reads as one object rather than as a drawing glued to a grey
             block. */
          const paint = readColor(resolveTint(look["--forge-wiki-card-tint"]));
          paintOn(edgeMat.color, paint, 0.62);
          edgeMat.opacity = weight * back;
          if (edgeMat.emissive) {
            edgeMat.emissive.setRGB(0, 0, 0);
            edgeMat.emissiveIntensity = 0;
          }
        }
      }

      /* The four boxes: how far each comes out of the card, and what colour.
         A box is scaled rather than rebuilt - it is one unit deep and its
         scale is how many units of that it wants - so a count changing costs
         a number rather than a geometry. */
      if (sevs) {
        const counts = tile.sevs || {};
        sevs.children.forEach(box => {
          const n = Number(counts[box.name]) || 0;
          /* Nothing found is nothing to show.
             -------------------------------------------------------------
             A zero used to be a flush, faint box, on the reasoning that four
             counters that never move are easier to scan than three that do.
             They still never move - each one keeps its own slot whether its
             neighbours are there or not - but an empty one is simply absent,
             so what is sticking out of a shelf of cards is only ever
             something that was actually found. */
          box.visible = n > 0;
          if (!n) return;

          const out = sevOut(n);
          box.scale.z = out;
          box.position.z = (depth > 0.5 ? depth / 2 : 0) + out / 2;
          paintOn(box.material.color, readColor(sevColour(box.name)));
          /* Solid.
             -------------------------------------------------------------
             These were painted at 62% so the card's own bottom line could be
             read through them. What that actually cost was the one thing a
             shelf of three hundred cards is scanned for: a colour at 62% over
             a tinted card is no longer that severity's colour, and four of
             them at four different depths were four different washes of it.
             They are opaque now and the line behind them is the one thing
             they stand in front of. `back` stays - that is the whole card
             stepping back for a search or a lens, which is not the same
             thing as this bar being see-through. */
          box.material.opacity = back;
        });
      }
      texture.needsUpdate = true;
    };
    w.one = one;

    const paintFaces = () => {
      tiles.forEach((unused, i) => one(i, i === w.hover ? 0 : null));
      w.dirty = true;
    };

    paintFaces();
    return () => cancelAnimationFrame(again);
  /* `depth` and `lit` are in here because they rebuild the scene: the pool
     above makes fresh meshes and this is what puts the drawings back on
     them. */
  }, [tiles, looks, query, selected, tex, enabled, depth, lit, glass]);

  /* ---- the hand, on cards that are not elements ----
     The orbit owns the drag; what is left is a press that did NOT travel,
     which is a click on whatever is under it, and the hover the preview column
     beside the stage follows. */
  useEffect(() => {
    const stage = stageRef.current;
    if (!enabled || !stage) return undefined;

    let moveAt = 0;
    let over = -1;
    let downX = 0;
    let downY = 0;
    let travelled = false;

    const at = (e) => {
      const w = world.current;
      if (!w || !w.ready) return -1;
      const box = stage.getBoundingClientRect();
      w.pointer.set(
        ((e.clientX - box.left) / box.width) * 2 - 1,
        -((e.clientY - box.top) / box.height) * 2 + 1,
      );
      w.raycaster.setFromCamera(w.pointer, w.camera);
      const hit = w.raycaster.intersectObjects(w.bodies.slice(0, w.shown || 0), false)[0];
      return hit ? hit.object.userData.index : -1;
    };

    const onDown = (e) => { downX = e.clientX; downY = e.clientY; travelled = false; };

    /* A ray per pointer event is a ray per pixel moved; one per frame is
       enough for a preview that follows the hand. */
    const onMove = (e) => {
      if (Math.hypot(e.clientX - downX, e.clientY - downY) > 4) travelled = true;
      if (moveAt) return;
      moveAt = requestAnimationFrame(() => {
        moveAt = 0;
        const w = world.current;
        if (!w || w.turning) return;
        if (e.target.closest && e.target.closest(".forge-wiki-card")) return;
        const i = at(e);
        if (i === over) return;
        over = i;
        /* the preview column beside the stage follows this, and so does the
           light running round the card itself */
        w.hover = i;
        w.dirty = true;
        onHover?.(i);
        stage.style.cursor = i >= 0 ? "zoom-in" : "";
      });
    };

    const onLeave = () => {
      over = -1;
      const w = world.current;
      if (w) { w.hover = -1; w.dirty = true; }
      onHover?.(-1);
      stage.style.cursor = "";
    };

    /* The click that follows a drag is not a click on anything: a pointer that
       travelled across a card was turning the scene. */
    const onClick = (e) => {
      if (travelled) { travelled = false; return; }
      if (e.target.closest && e.target.closest(".forge-wiki-card")) return;
      const i = at(e);
      if (i >= 0) onPick?.(e, i);
    };

    const onDouble = (e) => {
      if (e.target.closest && e.target.closest(".forge-wiki-card")) return;
      const i = at(e);
      if (i >= 0) onOpen?.(e, i);
    };

    const onContext = (e) => {
      if (!onMenu) return;
      if (e.target.closest && e.target.closest(".forge-wiki-card")) return;
      const i = at(e);
      if (i < 0) return;
      e.preventDefault();
      onMenu(e, i);
    };

    stage.addEventListener("pointerdown", onDown);
    stage.addEventListener("pointermove", onMove);
    stage.addEventListener("pointerleave", onLeave);
    stage.addEventListener("click", onClick);
    stage.addEventListener("dblclick", onDouble);
    stage.addEventListener("contextmenu", onContext);

    return () => {
      cancelAnimationFrame(moveAt);
      stage.removeEventListener("pointerdown", onDown);
      stage.removeEventListener("pointermove", onMove);
      stage.removeEventListener("pointerleave", onLeave);
      stage.removeEventListener("click", onClick);
      stage.removeEventListener("dblclick", onDouble);
      stage.removeEventListener("contextmenu", onContext);
      stage.style.cursor = "";
    };
  }, [stageRef, enabled, count, onPick, onOpen, onHover, onMenu]);

  /* ---- the surface the core paints through ---- */
  const surface = useMemo(() => ({
    /* The camera is this file's, not the core's: no pointer handlers on the
       stage, no tween onto a camera it does not own, no tour of its own. */
    ownsCamera: true,
    fit: (ms) => fit(ms),
    focus: (i, o) => focus(i, o),
    autoplay: (on) => tour(on),

    count: () => held.current.tiles.length,

    /* The page's own furniture, which sits inside the stage and whose events
       are not the stage's to take. The canvas is not furniture. */
    guard: (e) => !!(e.target.closest
      && e.target.closest(".forge-wiki-stack-doc, .forge-stack-list")),

    cardAt: () => -1,

    /* Where the cards are is the core's; when to paint them is this file's, so
       a frame is one draw whether it was the cards that moved or the camera. */
    paint: (cards, cam, blend) => {
      const w = world.current;
      if (!w) return;
      w.at = cards;
      w.blend = blend;
      w.dirty = true;
    },
  }), [fit, focus, tour]);

  core.current = useForgeStageCore(stageRef, { ...options, count, enabled, follow, selected, surface });

  /* The camera handles the page calls are this file's; everything else - the
     objects, the arrangement, the selection - is the core's. */
  return {
    ...core.current,
    resetCamera,
    fitToScreen: fit,
    focusCard: focus,
    bringIntoView,
    project: (o) => world.current?.project?.(o) || null,
  };
}

export default useForgeGpuStage;
